import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "../trpc.js";
import { Event } from "../constants/events.js";
import { captureEvent } from "../utils/posthog.js";
import { useNavigateMap } from "../routes/app/map/hooks/navigate.js";
import { useMapkaNotification } from "./notifications.js";

interface NewMapFormAndMenuDuplicateSharedParams {
  displayName: string;
  description?: string;
  sourceStyleId: string | number;
}

export const useHandleStyleCreation = (
  onError?: (errorMessage: string) => void,
  onComplete?: () => void,
) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const navigateMap = useNavigateMap();
  const mapkaNotification = useMapkaNotification();

  const {
    mutateAsync: createNewStyle,
    isPending,
  } = useMutation(trpc.createNewStyle.mutationOptions());

  const createStyle = useCallback((styleData: NewMapFormAndMenuDuplicateSharedParams) => {
    const { displayName, sourceStyleId, description } = styleData;
    captureEvent(Event.MapCreateNewMap);

    void createNewStyle({
      displayName,
      description,
      sourceStyleId: Number(sourceStyleId),
      spec: undefined,
    }).then((response) => {
      navigateMap(response.account.name, response.style.key);
      captureEvent(Event.MapCreateNewMapSuccess);
      mapkaNotification({
        title: intl.formatMessage({
          defaultMessage: "Map created",
          id: "notification.mapCreated",
        }),
        message: intl.formatMessage({
          defaultMessage: "Map {displayName} has been created successfully",
          id: "notification.mapCreatedMessage",
        },
        {
          displayName: response.style.displayName,
        },
        ),
        variant: "success",
      });

      onComplete?.();
    }).catch((error: Error) => {
      onError?.(error.message);
    });
  }, [intl, onError, onComplete, navigateMap, createNewStyle]);

  return { createStyle, isPending };
};
